import React, { useState, useEffect, useRef } from 'react';
import {
  getAuth,
  sendEmailVerification,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  reload,
} from 'firebase/auth';
import { Mail, Phone, Shield, CheckCircle, AlertTriangle, RefreshCw, Clock, User, LogOut } from 'lucide-react';
import { getAppDomain } from '../utils/domainConfig';
import { db, doc, setDoc, serverTimestamp } from '../firebase.config';
import { toE164Phone, getPhoneAuthErrorMessage } from '../utils/firebasePhoneAuth';

const VerificationRequired = ({ user, onVerificationComplete }) => {
  const profilePhone = user?.phoneNumber || user?.phone || '';
  const [verificationMethod, setVerificationMethod] = useState(profilePhone ? 'phone' : 'email');
  const [phoneNumber, setPhoneNumber] = useState(profilePhone);
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const auth = getAuth();
  
  // Get the actual Firebase Auth user object
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);
  
  // State to track if auth is ready
  const [authReady, setAuthReady] = useState(false);
  
  // Wait for auth to be ready
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setFirebaseUser(user);
      setAuthReady(true);
    });
    
    // Also check current state immediately
    if (auth.currentUser) {
      setFirebaseUser(auth.currentUser);
      setAuthReady(true);
    }
    
    return unsubscribe;
  }, []);

  // Prefill phone from profile / Auth when available
  useEffect(() => {
    const fromProp = user?.phoneNumber || user?.phone || '';
    const fromAuth = firebaseUser?.phoneNumber || '';
    const next = fromAuth || fromProp;
    if (next && !phoneNumber) {
      setPhoneNumber(next);
      setVerificationMethod('phone');
    }
  }, [user?.phoneNumber, user?.phone, firebaseUser?.phoneNumber]);

  // Countdown timer for resend button - also check persistent rate limit
  useEffect(() => {
    // Check persistent rate limit on mount
    if (firebaseUser?.uid) {
      const rateLimitKey = `email_verification_rate_limit_${firebaseUser.uid}`;
      const lastSentTime = localStorage.getItem(rateLimitKey);
      if (lastSentTime) {
        const now = Date.now();
        const elapsed = now - parseInt(lastSentTime);
        const RATE_LIMIT_MS = 60000; // 60 seconds
        if (elapsed < RATE_LIMIT_MS) {
          const remainingSeconds = Math.ceil((RATE_LIMIT_MS - elapsed) / 1000);
          setCountdown(remainingSeconds);
        }
      }
    }
    
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown, firebaseUser?.uid]);

  // Check current verification status
  // NOTE: Only check email verification or Google sign-in
  // Phone number from Firestore is NOT considered verification
  // Only phone verified via Firebase Phone Auth (in Firebase Auth) counts
  // IMPORTANT: Use a ref to prevent multiple redirects
  const hasRedirectedRef = useRef(false);
  
  useEffect(() => {
    if (!firebaseUser || !authReady || hasRedirectedRef.current) return;
    
    // Only check Firebase Auth user, not the user prop (which might have stale data)
    const isCurrentlyVerified = firebaseUser.emailVerified || 
                                 firebaseUser.providerData?.some(p => p.providerId === 'google.com') ||
                                 firebaseUser.phoneNumber; // Only if verified via Firebase Phone Auth
    
    if (isCurrentlyVerified) {
      hasRedirectedRef.current = true; // Prevent multiple redirects
      console.log('✅ User is verified, redirecting to dashboard...');
      // Add a small delay to prevent immediate redirect loops
      const redirectTimeout = setTimeout(() => {
        onVerificationComplete();
      }, 1000);
      
      return () => clearTimeout(redirectTimeout);
    }
  }, [firebaseUser?.emailVerified, firebaseUser?.providerData, firebaseUser?.phoneNumber, authReady, onVerificationComplete]);

  // Check verification status manually
  const checkVerificationStatus = async () => {
    if (!firebaseUser) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Force reload user from Firebase
      await reload(firebaseUser);
      
      // Get fresh user data
      const currentUser = auth.currentUser;
      
      // Only check email verification or Google sign-in
      // Phone number only counts if verified via Firebase Phone Auth
      const isVerified = currentUser?.emailVerified || 
                        currentUser?.providerData?.some(p => p.providerId === 'google.com') ||
                        currentUser?.phoneNumber; // Only if verified via Firebase Phone Auth
      
      if (isVerified) {
        setMessage('✅ Account verified successfully! Redirecting to dashboard...');
        
        setTimeout(() => {
          onVerificationComplete();
        }, 1500);
      } else {
        setError('❌ Account not yet verified. Please check your email and click the verification link, then try again.');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setError('❌ Error checking verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearRecaptcha = () => {
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }
    } catch (_) {
      /* ignore */
    }
    window.recaptchaVerifier = null;
    const el = document.getElementById('recaptcha-container');
    if (el) el.innerHTML = '';
  };

  const setupRecaptcha = () => {
    clearRecaptcha();
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'normal',
      callback: () => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
        clearRecaptcha();
      },
    });
    return window.recaptchaVerifier;
  };

  const sendEmailVerificationLink = async () => {
    if (!firebaseUser) {
      setError('❌ No authenticated user found. Please login again.');
      return;
    }

    if (!firebaseUser.email) {
      setError('❌ No email address found for this user. Please contact support.');
      return;
    }

    // Additional validation
    if (typeof firebaseUser.getIdToken !== 'function') {
      console.error('❌ Firebase user object is invalid:', firebaseUser);
      setError('❌ Invalid user object. Please refresh the page and try again.');
      return;
    }

    // Check persistent rate limiting (survives page refresh)
    const rateLimitKey = `email_verification_rate_limit_${firebaseUser.uid}`;
    const lastSentTime = localStorage.getItem(rateLimitKey);
    const now = Date.now();
    const RATE_LIMIT_MS = 60000; // 60 seconds minimum between requests
    
    // Check if this is the first request after signup (email wasn't sent during signup)
    const emailNotSentFlag = sessionStorage.getItem(`verification_email_not_sent_${firebaseUser.uid}`);
    
    // If email wasn't sent during signup, allow first request without rate limit check
    // But still check if there was a recent manual request
    if (!emailNotSentFlag && lastSentTime && (now - parseInt(lastSentTime)) < RATE_LIMIT_MS) {
      const remainingSeconds = Math.ceil((RATE_LIMIT_MS - (now - parseInt(lastSentTime))) / 1000);
      setError(`❌ Please wait ${remainingSeconds} seconds before requesting another verification email.`);
      setCountdown(remainingSeconds);
      return;
    }

    // Prevent multiple simultaneous calls
    if (loading) {
      setError('❌ Verification email is already being sent. Please wait...');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const actionCodeSettings = {
        url: `${getAppDomain()}/#/login`,
        handleCodeInApp: false,
      };
      
      await sendEmailVerification(firebaseUser, actionCodeSettings);
      
      // Clear the "not sent" flag since we just sent it
      sessionStorage.removeItem(`verification_email_not_sent_${firebaseUser.uid}`);
      
      // Store timestamp in localStorage for persistent rate limiting
      localStorage.setItem(rateLimitKey, now.toString());
      
      setEmailSent(true);
      setCountdown(60); // Start 60 second countdown
      setMessage(`✅ Verification email sent to ${firebaseUser.email}. Please check your inbox and click the verification link.`);
    } catch (error) {
      console.error('Error sending email verification:', error);
      
      if (error.code === 'auth/too-many-requests') {
        // Firebase's server-side rate limit - store timestamp to prevent retries
        localStorage.setItem(rateLimitKey, now.toString());
        
        // Check if this might be due to automatic send during signup
        // If so, provide a more helpful message
        const timeSinceSignup = sessionStorage.getItem(`signup_time_${firebaseUser.uid}`);
        if (timeSinceSignup && (now - parseInt(timeSinceSignup)) < 120000) {
          // Signup happened less than 2 minutes ago
          setError('❌ Verification email was already sent during signup. Please check your inbox. If you don\'t see it, wait 60 seconds and try again.');
        } else {
          setError('❌ Too many requests. Please wait at least 60 seconds before trying again.');
        }
        setCountdown(60);
      } else if (error.code === 'auth/network-request-failed') {
        setError('❌ Network error. Please check your internet connection.');
      } else if (error.message && error.message.includes('getIdToken is not a function')) {
        setError('❌ Authentication error. Please refresh the page and try again.');
      } else {
        setError(`❌ Failed to send verification email: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneVerification = async () => {
    if (!phoneNumber) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!firebaseUser && !auth.currentUser) {
      setError('No authenticated user found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const formattedPhone = toE164Phone(phoneNumber);
      if (!/^\+[1-9]\d{7,14}$/.test(formattedPhone)) {
        setError('Please enter a valid phone number (e.g. 9876543210 or +91…).');
        return;
      }

      const appVerifier = setupRecaptcha();
      const target = auth.currentUser || firebaseUser;

      // Already signed in → link phone to this account (do not start a separate phone sign-in)
      let confirmation;
      try {
        confirmation = await linkWithPhoneNumber(target, formattedPhone, appVerifier);
      } catch (linkErr) {
        // Fallback only when linking is not applicable
        if (
          linkErr?.code === 'auth/provider-already-linked' ||
          linkErr?.code === 'auth/credential-already-in-use'
        ) {
          throw linkErr;
        }
        console.warn('linkWithPhoneNumber failed, falling back to signInWithPhoneNumber:', linkErr?.code || linkErr);
        confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      }

      setConfirmationResult(confirmation);
      setMessage('SMS verification code sent to your phone.');
    } catch (error) {
      console.error('Error sending phone verification:', error);
      clearRecaptcha();
      setConfirmationResult(null);
      setError(getPhoneAuthErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (!verificationCode || !confirmationResult) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmationResult.confirm(verificationCode);
      await reload(auth.currentUser);

      const linkedPhone = auth.currentUser?.phoneNumber || toE164Phone(phoneNumber);
      const uid = auth.currentUser?.uid;

      // Mirror verified phone onto Firestore profile + phone_index
      if (uid && linkedPhone) {
        try {
          await setDoc(
            doc(db, 'users', uid),
            {
              phone: linkedPhone,
              phoneNumber: linkedPhone,
              phoneVerified: true,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          await setDoc(
            doc(db, 'phone_index', linkedPhone),
            {
              uid,
              email: auth.currentUser?.email || '',
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          if (/^\+91\d{10}$/.test(linkedPhone)) {
            await setDoc(
              doc(db, 'phone_index', linkedPhone.slice(3)),
              {
                uid,
                email: auth.currentUser?.email || '',
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }
        } catch (profileErr) {
          console.warn('Phone linked in Auth but profile update failed:', profileErr);
        }
      }

      clearRecaptcha();
      setMessage('✅ Phone number verified successfully! Redirecting...');
      setTimeout(() => onVerificationComplete(), 1500);
    } catch (error) {
      console.error('Error verifying phone code:', error);
      setError(getPhoneAuthErrorMessage(error) || '❌ Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const checkEmailVerification = async () => {
    if (!firebaseUser) {
      setError('❌ No authenticated user found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      await reload(firebaseUser);
      const currentUser = auth.currentUser;
      if (currentUser?.emailVerified) {
        setMessage('Email verified successfully!');
        setTimeout(() => onVerificationComplete(), 1500);
      } else {
        setError('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      setError('Error checking verification status');
    } finally {
      setLoading(false);
    }
  };

  // Show loading if auth is not ready
  if (!authReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <RefreshCw className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Loading...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Initializing authentication...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no Firebase user found
  if (!firebaseUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Authentication Error
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              No authenticated user found. Please login again.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={handleLogout}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if Firebase user is invalid
  if (typeof firebaseUser.getIdToken !== 'function' || typeof firebaseUser.reload !== 'function') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-10 w-10 text-orange-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Invalid User Object
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              The user object is missing required methods. This may be a Firebase configuration issue.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if sendEmailVerification function is not available
  if (typeof sendEmailVerification !== 'function') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Function Import Error
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              The sendEmailVerification function is not properly imported. This is a code issue.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          {/* Animated shield icon */}
          <div className="relative mx-auto w-24 h-24 sm:w-28 sm:h-28 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-3xl rotate-6 opacity-20"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-xl">
              <Shield className="h-12 w-12 sm:h-14 sm:w-14 text-white" />
            </div>
            {/* Decorative ring */}
            <div className="absolute -inset-2 border-2 border-dashed border-blue-300 rounded-full opacity-50 animate-spin" style={{ animationDuration: '20s' }}></div>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Account Verification
          </h2>
          <p className="mt-2 text-sm text-gray-600 max-w-xs mx-auto">
            Secure your account to access your ACCTOO dashboard
          </p>
          
          {/* User Info Card */}
          <div className="mt-5 bg-white rounded-2xl p-4 shadow-md border border-gray-100">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-md">
                {(firebaseUser?.email || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-xs text-gray-500">Signed in as</div>
                <div className="text-sm font-semibold text-gray-800 truncate">{firebaseUser?.email || user?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 active:bg-red-100 rounded-xl transition-colors touch-manipulation"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Verification Card */}
        <div className="bg-white shadow-xl rounded-2xl p-5 sm:p-6 border border-gray-100">

          {/* Status Messages */}
          {message && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                <span className="text-sm text-green-700">{message}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Verification Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              Choose verification method:
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setVerificationMethod('email')}
                className={`relative flex flex-col items-center justify-center px-3 py-5 rounded-2xl border-2 transition-all min-h-[90px] touch-manipulation ${
                  verificationMethod === 'email'
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-2 ${
                  verificationMethod === 'email'
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  <Mail className="w-6 h-6" />
                </div>
                <span className="text-sm font-bold">Email</span>
                <span className="text-[10px] text-gray-400">Verification link</span>
                {verificationMethod === 'email' && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => setVerificationMethod('phone')}
                className={`relative flex flex-col items-center justify-center px-3 py-5 rounded-2xl border-2 transition-all min-h-[90px] touch-manipulation ${
                  verificationMethod === 'phone'
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 text-green-700 shadow-md'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-2 ${
                  verificationMethod === 'phone'
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  <Phone className="w-6 h-6" />
                </div>
                <span className="text-sm font-bold">Phone</span>
                <span className="text-[10px] text-gray-400">SMS code</span>
                {verificationMethod === 'phone' && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full"></div>
                )}
              </button>
            </div>
          </div>

          {/* Email Verification */}
          {verificationMethod === 'email' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">
                      {firebaseUser?.email || user?.email}
                    </span>
                  </div>
                  {firebaseUser?.emailVerified && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>
              </div>

              {!emailSent ? (
                <button
                  onClick={sendEmailVerificationLink}
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Send Verification Email
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Email sent successfully!</p>
                        <p className="text-xs text-green-600 mt-1">Check your inbox and click the verification link.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-2">📧 Next Steps:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Check your email inbox (including spam folder)</li>
                        <li>Click on the verification link in the email</li>
                        <li>Click the "Verify Me" button below after clicking the link</li>
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={checkVerificationStatus}
                      disabled={loading}
                      className="w-full flex justify-center items-center py-3 px-4 border border-blue-300 rounded-lg shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                    >
                      {loading ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Verify Me
                    </button>
                    
                    <div className="text-xs text-gray-500 text-center">
                      Click "Verify Me" after clicking the verification link in your email
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={() => {
                        setEmailSent(false);
                        setCountdown(0);
                      }}
                      disabled={countdown > 0}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 transition-colors"
                    >
                      {countdown > 0 ? (
                        <span className="flex items-center justify-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Resend in {countdown}s
                        </span>
                      ) : (
                        'Resend verification email'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phone Verification */}
          {verificationMethod === 'phone' && (
            <div className="space-y-4">
              {!confirmationResult ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+91 9876543210"
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div id="recaptcha-container"></div>
                  <button
                    onClick={sendPhoneVerification}
                    disabled={loading || !phoneNumber}
                    className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors touch-manipulation min-h-[56px]"
                  >
                    {loading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Phone className="w-5 h-5 mr-2" />
                        Send SMS Code
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter 6-digit code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      maxLength="6"
                      className="w-full px-4 py-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-[0.5em] font-mono"
                    />
                  </div>
                  <button
                    onClick={verifyPhoneCode}
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors touch-manipulation min-h-[56px]"
                  >
                    {loading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Verify Code
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setConfirmationResult(null);
                      setVerificationCode('');
                      setError('');
                      clearRecaptcha();
                    }}
                    className="w-full text-sm text-blue-600 hover:text-blue-500 active:text-blue-700 py-3 touch-manipulation min-h-[44px]"
                  >
                    Use different phone number
                  </button>
                </>
              )}
            </div>
          )}

          {/* Messages */}
          {message && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                <span className="text-sm text-green-700">{message}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 rounded-md">
              <div className="flex items-center">
                <AlertTriangle className="w-4 h-4 text-red-400 mr-2" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
              <div className="text-sm text-gray-600">
                <strong>Why verification is required:</strong>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Protect your business data</li>
                  <li>Prevent unauthorized access</li>
                  <li>Comply with security standards</li>
                  <li>Enable account recovery options</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationRequired;
